/**
 * Analytics utility for Google Analytics 4
 * Tracks page views, conversions, and custom events
 */

// Check if gtag is available
const isGtagAvailable = () => typeof window !== 'undefined' && typeof window.gtag === 'function';

/**
 * Track a custom event
 * @param {string} eventName - Name of the event (e.g., 'cta_click', 'form_submit')
 * @param {object} params - Event parameters
 */
export const trackEvent = (eventName, params = {}) => {
  if (isGtagAvailable()) {
    window.gtag('event', eventName, params);
  }
  // Also log to console in development
  if (process.env.NODE_ENV === 'development') {
  }
};

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Track a page view (both GA4 + server-side)
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (isGtagAvailable()) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle,
    });
  }
  // Server-side tracking for analytics dashboard
  try {
    fetch(`${API}/api/track/pageview`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ page: pagePath || window.location.pathname, referrer: document.referrer }),
      keepalive: true
    }).catch(() => {});
  } catch (e) { /* non-blocking */ }
};

/**
 * Track CTA button clicks
 * @param {string} ctaName - Name/identifier of the CTA
 * @param {string} location - Where on the page the CTA is located
 */
export const trackCTAClick = (ctaName, location) => {
  trackEvent('cta_click', {
    cta_name: ctaName,
    cta_location: location,
    page_path: window.location.pathname,
  });
};

/**
 * Track conversion events (sign ups, purchases, etc.)
 * @param {string} conversionType - Type of conversion
 * @param {object} details - Additional details
 */
export const trackConversion = (conversionType, details = {}) => {
  trackEvent('conversion', {
    conversion_type: conversionType,
    ...details,
  });
};

/**
 * Track scroll depth
 * @param {number} percentage - Scroll depth percentage
 */
export const trackScrollDepth = (percentage) => {
  trackEvent('scroll_depth', {
    depth_percentage: percentage,
    page_path: window.location.pathname,
  });
};

/**
 * Track form interactions
 * @param {string} formName - Name of the form
 * @param {string} action - Action taken (start, complete, abandon)
 */
export const trackFormInteraction = (formName, action) => {
  trackEvent('form_interaction', {
    form_name: formName,
    form_action: action,
  });
};

/**
 * Track navigation clicks
 * @param {string} linkName - Name of the navigation link
 * @param {string} destination - Where the link goes
 */
export const trackNavClick = (linkName, destination) => {
  trackEvent('navigation_click', {
    link_name: linkName,
    destination: destination,
  });
};

/**
 * Track user sign up
 * @param {string} method - Sign up method (email, google, etc.)
 */
export const trackSignUp = (method) => {
  trackEvent('sign_up', {
    method: method,
  });
  trackConversion('sign_up', { method });
};

/**
 * Track user login
 * @param {string} method - Login method
 */
export const trackLogin = (method) => {
  trackEvent('login', {
    method: method,
  });
};

/**
 * Set user properties for better segmentation
 * @param {object} properties - User properties to set
 */
export const setUserProperties = (properties) => {
  if (isGtagAvailable()) {
    window.gtag('set', 'user_properties', properties);
  }
};

export default {
  trackEvent,
  trackPageView,
  trackCTAClick,
  trackConversion,
  trackScrollDepth,
  trackFormInteraction,
  trackNavClick,
  trackSignUp,
  trackLogin,
  setUserProperties,
};
